/**
 * Pantalla de **gestión de atendidos** (`/admin/atendidos`). Reemplaza el
 * placeholder de `ROUTES.atendidos`. Equivale a `atendidos.html` del SSR.
 *
 * Solo-admin (permiso `all`, mismo que la API y el menú del SSR) vía
 * `RequireAdmin`. Orquesta:
 *  - la lista de atendidos de la **sede activa** (`useAtendidos`) en `AtendidosTable`;
 *  - alta/edición (`AtendidoFormDialog`) y borrado con confirmación
 *    (`ConfirmDeleteDialog`).
 *
 * El listado lo acota la sede activa (`X-Sede-Id`, inyectado por `lib/api`), igual
 * que el SSR filtra por la sede de la sesión. Un admin multi-sede que aún no ha
 * elegido sede no puede listar (la API daría 400): se le invita a elegir sede en
 * el selector del encabezado, en vez de mostrar un error técnico.
 *
 * Presentacional + hooks; sin `fetch`. Estados de carga/error contemplados.
 */
import { useState } from "react";

import { Button, Card, CardContent } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { adminErrorMessage } from "../format";
import { useAtendidos } from "../hooks/useAdminData";
import { useDeleteAtendido } from "../hooks/useAtendidoMutations";
import type { AtendidoListItem } from "../types";
import { AtendidoFormDialog } from "./AtendidoFormDialog";
import { AtendidosTable } from "./AtendidosTable";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { RequireAdmin } from "./RequireAdmin";

/** Objetivo del formulario de atendido (alta o edición). */
type FormTarget =
  | { mode: "create" }
  | { mode: "edit"; atendido: AtendidoListItem }
  | null;

export function AtendidosScreen() {
  const { isMultiSede, activeSede } = useAuth();
  const atendidosQuery = useAtendidos();
  const deleteAtendido = useDeleteAtendido();

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<AtendidoListItem | null>(null);

  // Un admin multi-sede sin sede elegida no puede listar atendidos (la API exige
  // X-Sede-Id): se le pide elegir sede en el selector del encabezado.
  const needsSede = isMultiSede && activeSede === null;

  /** Abre el diálogo de borrado limpiando un posible error de un intento previo. */
  function openDelete(atendido: AtendidoListItem) {
    deleteAtendido.reset();
    setDeleteTarget(atendido);
  }

  function handleConfirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    deleteAtendido.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <RequireAdmin
      title="Atendidos"
      deniedMessage="No tienes permiso para gestionar atendidos."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-2xl font-semibold text-slate-900">Atendidos</h1>
            {activeSede && (
              <p className="text-sm text-slate-500">
                Sede activa: <span className="font-medium">{activeSede.nombre}</span>
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="primary"
            disabled={needsSede}
            onClick={() => setFormTarget({ mode: "create" })}
          >
            Nuevo atendido
          </Button>
        </div>

        {needsSede ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Selecciona una sede en el encabezado para gestionar sus atendidos.
              </p>
            </CardContent>
          </Card>
        ) : atendidosQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando atendidos…
              </p>
            </CardContent>
          </Card>
        ) : atendidosQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {adminErrorMessage(atendidosQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <AtendidosTable
                atendidos={atendidosQuery.data.items}
                onEdit={(atendido) => setFormTarget({ mode: "edit", atendido })}
                onDelete={openDelete}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <AtendidoFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        title="Eliminar atendido"
        description={
          deleteTarget
            ? `¿Eliminar al atendido "${deleteTarget.nombre}"? Esta acción no se puede deshacer.`
            : ""
        }
        errorMessage={
          deleteAtendido.isError ? adminErrorMessage(deleteAtendido.error) : null
        }
        isPending={deleteAtendido.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </RequireAdmin>
  );
}
